import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Input,
  Label,
  Container,
  CardFooter,
  Badge,
  UncontrolledTooltip,
  Col,
  Row,
} from "reactstrap";
import RightPanelCard from "components/CommonComponents/RightPanelCard";
import CommonTable from "components/CommonComponents/ReactTable/Table";
import { DEFAULT_RECORDS_PER_PAGE, STATUS_OPTIONS } from "components/constant";
import Select, { components } from "react-select";
import { fixedWidthCustomStyle } from "constants/CommonConstants";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";
import { showAlert } from "components/CommonComponents/CommonFunctions";
import AdminHeader from "components/Headers/AdminHeader";
import { addSkill, getSkillList, updateSkillById } from "action/skills";
import { useDispatch, useSelector } from "react-redux";
import { skillsList } from "constants/skills";
import { REGEX } from "constants/CommonConstants";

const initialFields = {
  name: { 
    value: "",
     error: "",
     isChanged: false
     },
  status:{ 
    value: "ACTIVE",
     error: "",
     isChanged:false,
     },
};

function Skills() {
  const dispatch = useDispatch();
  const skillsData = useSelector((state) => state.skills.skillsList);
  const { notify } = useContext(NotificationContext);
  const [isModalOpen, setIsModalOpen] = useState(null); //"add "edit" "null"
  const [formData, setFormData] = useState(initialFields);
  const [loaderOnClick, setLoaderOnClick] = useState(false);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    isSearching: false,
    isUpdatingStatus: false,
  });
  const [pageNum, setPageNum] = useState(1);
  const [searchState, setSearchState] = useState({
    name: "",
    status: null,
    filterParams: [],
  });

  useEffect(() => {
    getSkillList(dispatch, pageNum, searchState.filterParams);
    return () => {
      dispatch({ type: skillsList, payload: {} });
    };
  }, [dispatch, pageNum, searchState.filterParams]);

  useEffect(() => {
    if (skillsData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: skillsData?.data?.skills?.length === 0,
        isSearching: false,
        isUpdatingStatus: false,
      });
    } else if (skillsData?.code || skillsData?.message) {
      setListState({
        isLoading: false,
        error:
          skillsData?.message ||
          "Failed to load skills list! Please try again.",
        noRecords: false,
        isSearching: false,
        isUpdatingStatus: false,
      });
    }
  }, [skillsData]);
  const formattedListData = skillsData?.data?.skills?.map((item, index) => ({
    id: item.ID,
    name: item.name,
    status: item.status,
    srNo:
      (pageNum - 1) *
        (skillsData?.data?.noOfRecordsPerPage || DEFAULT_RECORDS_PER_PAGE) +
      index +
      1,
  }));

  const openAddModal = () => {
    // e.stopPropagation();
    setIsModalOpen("add");
    setFormData(initialFields);
  };

  const openEditModal = (skill) => {
    setIsModalOpen("edit");
    setFormData({
      id: skill.id,
      name: { value: skill.name, error: "" },
      status: { value: skill.status, error: "" },
    });
  };

  const closeModal = () => {
    setIsModalOpen(null);
    setFormData({
      ...initialFields,
    });
  };
 const getEditedBodyData = (formData) =>
    Object.fromEntries(
      Object.entries(formData)
        .filter(([, field]) => field.isChanged) // only changed fields
        .map(([key, field]) => [key, field.value]),
    );

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    const isEdit = isModalOpen === "edit";
    const bodyData = isEdit
    ?  getEditedBodyData(formData)
    : {
      name: formData.name.value,
      status: formData.status.value,
    };
  

  if (isEdit && Object.keys(bodyData).length === 0) {
      notify("info", "No changes detected to update.");
      return;
    }
    try {
      setLoaderOnClick(true);
  
      const res = isEdit
        ? await updateSkillById(formData.id, bodyData)
        : await addSkill(bodyData);
      if (res?.code === 200) {
        notify(
          "success",
          res?.message || `Skill ${isEdit ? "updated" : "added"} successfully`,
        );

        closeModal();

        pageNum === 1
          ? getSkillList(dispatch, pageNum, searchState.filterParams)
          : setPageNum(1);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || `Failed to ${isEdit ? "update" : "add"} skill`;

        notify("danger", message);
      }
    } catch (error) {
      notify(
        "danger",
        `Failed to ${isModalOpen === "edit" ? "update" : "add"} skill`,
      );
    } finally {
      setLoaderOnClick(false);
    }
  };

  const toggleStatus = (skill) => {
    const isActive = skill.status === "ACTIVE";
    const newStatus = isActive ? "INACTIVE" : "ACTIVE";

    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactive" : "activate"
      } this skill?`,
      message: `Skill "${
        skill.name
      }" will become ${newStatus.toLowerCase()}.`,
      type: "warning",
      confirmStyle: "deep-blue",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: async (closeAlert) => {
        closeAlert();
        try {
          setListState((prev) => ({
            ...prev,
            isUpdatingStatus: true,
          }));

          const res = await updateSkillById(skill.id, {
            name: skill.name,
            status: newStatus,
          });
          if (res?.code === 200) {
            notify("success", res?.message || "Status updated successfully");

            getSkillList(dispatch, pageNum, searchState.filterParams);
          }
        } catch {
          notify("danger", "Failed to update status");
        } finally {
          setListState((prev) => ({
            ...prev,
            isUpdatingStatus: false,
          }));
        }
      },
    });
  };

  const columns = [
    {
      Header: "Sr. No.",
      accessor: "srNo",
      centerAlign: true,
      minWidth: 50,
      maxWidth: 60,
    },
    {
      Header: "Skill Name",
      accessor: "name",
      minWidth: 210,
      maxWidth: 210,
      Cell: ({ value }) => <div className="text-wrapper">{value}</div>,
    },
    {
      Header: "Status",
      accessor: "status",
      centerAlign: true,
      minWidth: 110,
      maxWidth: 110,
      Cell: ({ value }) => (
        <Badge color={value === "ACTIVE" ? "success" : "warning"} pill>
          {value}
        </Badge>
      ),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => {
        const skill = row.original;
        const id = skill.id;
        const status = skill.status;
        const isActive = status === "ACTIVE";

        return (
          <div className="d-flex justify-content-start align-items-center">
            {/* EDIT */}
            <Button
              size="sm"
              color="link"
              className="p-0"
              onClick={() => openEditModal(skill)}
              id={`edit${id}`}
            >
              <i className="fas fa-edit mr-1 table-action-info" />
              <UncontrolledTooltip delay={0} target={`edit${id}`}>
                Edit
              </UncontrolledTooltip>
            </Button>
            {/* Divider */}
            <span className="mr-1 text-muted">|</span>
            {/* Activate / Deactivate */}
            <Button
              color="link"
              size="sm"
              className="d-flex align-items-center p-0 ml-1"
              id={`status${id}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleStatus(skill);
              }}
            >
              <i
                className={`fas status-toggle-icon ${
                  isActive
                    ? "fa-toggle-on text-success"
                    : "fa-toggle-off text-danger"
                }`}
              />
            </Button>
            <UncontrolledTooltip delay={0} target={`status${id}`}>
              {isActive ? "Inactivate" : "Activate"}
            </UncontrolledTooltip>
          </div>
        );
      },
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const hasChanged = prev[field]?.value !== value;

      return {
        ...prev,
        [field]: {
          ...prev[field],
          value,
          error: "",
          isChanged: hasChanged,
        },
      };
    });
  };

  // Custom Option Renderer
  const Option = (props) => {
    return (
      <components.Option {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.Option>
    );
  };

  // Custom SingleValue Renderer (for the selected value)
  const SingleValue = (props) => {
    return (
      <components.SingleValue {...props}>
        <Badge
          color={props.data.value === "ACTIVE" ? "success" : "warning"}
          pill
        >
          {props.data.label}
        </Badge>
      </components.SingleValue>
    );
  };

  const handleClear = () => {
    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    setSearchState({
      name: "",
      status: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const buildFilterParams = () => {
    const filters = [];

    if (searchState.name?.trim()) {
      filters.push({
        field: "Name",
        condition: "like",
        filterValues: [searchState.name.trim()],
      });
    }

    if (searchState.status?.value) {
      filters.push({
        field: "Status",
        condition: "eq",
        filterValues: [searchState.status.value],
      });
    }

    return filters.length ? JSON.stringify(filters) : [];
  };

  const handleSearch = () => {
    const newFilters = buildFilterParams();

    if (
      JSON.stringify(searchState.filterParams) !== JSON.stringify(newFilters)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        isSearching: true,
      }));
      setSearchState((prev) => ({
        ...prev,
        filterParams: newFilters,
      }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleSearchNameChange = (e) => {
    const value = e.target.value;

    if (value === "" || REGEX.ALPHANUMERIC_SPACE_REGEX.test(value)) {
      setSearchState((prev) => ({
        ...prev,
        name: value,
      }));
    }
  };

  const getValidationError = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "name":
        if (!trimmedValue) return "Please enter skill name";
        if (value.trim().length < 3)
          return "Skill name must be at least 3 characters";
        if (value.trim().length > 150)
          return "Skill name cannot exceed 150 characters";
        // if (!REGEX.ALPHANUMERIC_SPACE_REGEX.test(trimmedValue))
        //   return "Skill name can contain only letters and numbers";
        return "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    let newSkill = { ...formData };

    Object.keys(newSkill).forEach((key) => {
      if (key === "id") return;
      newSkill[key] = {
        ...newSkill[key],
        error: getValidationError(key, newSkill[key].value),
      };
    });

    setFormData(newSkill);
    return Object.entries(newSkill)
      .filter(([key]) => key !== "id")
      .every(([, item]) => item.error === "");
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  return (
    <>
      <AdminHeader />
      {listState.isUpdatingStatus && (
        <div className="logout-overlay">
          <div className="logout-card">
            <i className="fa fa-spinner fa-spin fa-2x text-deep-blue mb-4" />
            <div className="font-weight-bold text-dark">
              Updating status... Please wait.
            </div>
          </div>
        </div>
      )}
      <Container fluid className="mt--6">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex align-items-center justify-content-between">
                 <CardTitle tag="h3" className="mb-0 text-default">
                  Skills
                </CardTitle>

                <Button className="btn-deep-blue" onClick={openAddModal}>
                  <i className="fa fa-plus mr-1" />
                  Skill
                </Button>
              </CardHeader>
              {/* Body */}
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap flex-column flex-md-row">
                  {/* Left side - Filter Inputs + Search */}
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    <Input
                      type="text"
                      placeholder="Search by skill name"
                      className="mr-2 mb-2 filter-input-large"
                      onChange={handleSearchNameChange}
                      value={searchState.name}
                      onKeyDown={handleKeyDown}
                    />
                    <Select
                      className="mr-2 mb-2 filter-select"
                      options={STATUS_OPTIONS}
                      placeholder="Select status"
                      value={searchState.status}
                      onChange={(opt) =>
                        setSearchState((prev) => ({
                          ...prev,
                          status: opt,
                        }))
                      }
                      classNamePrefix="react-select"
                      styles={fixedWidthCustomStyle()}
                      components={{ Option, SingleValue }}
                    />

                    <Button
                      color="default"
                      // className="d-flex align-items-center btn-deep-blue mb-0"
                      className={`d-flex align-items-center mb-2 filter-btn btn-deep-blue ${listState.isSearching ? "search-btn" : ""}`}
                      onClick={handleSearch}
                    >
                      {listState.isSearching ? (
                        <i className="fa fa-spinner fa-spin loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {Object.keys(searchState.filterParams).length > 0 && (
                      <Button
                        color="default"
                        className="mb-2 btn-deep-blue filter-btn btn-simple"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" /> Clear
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-2 mb-3 mx-1">
                  {skillsData?.data?.totalRecords > 0 ? (
                    <>
                      {searchState.filterParams.length > 0 ? (
                        <span className="total-records">
                          Showing {skillsData?.data?.totalFilteredRecords} of{" "}
                          {skillsData?.data?.totalRecords} Records
                        </span>
                      ) : (
                        <span className="total-records">
                          Total Records : {skillsData?.data?.totalRecords}
                        </span>
                      )}
                    </>
                  ) : null}
                </div>

                <div className="table-responsive">
                  {listState.isLoading ? (
                    <div className="loaderContainer">
                      <i className="fa fa-spinner fa-spin fa-2xl loader" />
                    </div>
                  ) : listState.error ? (
                    <div className="loaderContainer error-style">
                      <div className="error-icon">
                        <i class="fa-solid fa-circle-exclamation"></i>
                      </div>
                      {listState.error}
                    </div>
                  ) : listState.noRecords ? (
                    <div className="loaderContainer empty-style">
                      <div className="empty-icon">
                        <i class="fa-solid fa-circle-info"></i>
                      </div>
                      <span>No skills found.</span>
                    </div>
                  ) : (
                    <CommonTable
                      columns={columns}
                      data={formattedListData || []}
                      pageNum={pageNum}
                      setPageNum={setPageNum}
                      defaultPageSize={DEFAULT_RECORDS_PER_PAGE}
                      showPaginationBottom
                      totalPages={Math.ceil(
                        skillsData?.data?.totalFilteredRecords /
                          skillsData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE,
                      )}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <RightPanelCard isOpen={isModalOpen !== null} onClose={closeModal}>
          <Card>
            <CardHeader tag="div" className="p-3">
              <div className="d-flex align-items-center justify-content-between mt-2">
                <div className="d-flex align-items-center">
                  <i
                    className="fa-solid fa-angle-left cursor mr-2"
                    onClick={closeModal}
                  ></i>
                  <h4 className="mb-0 text-default">
                    {isModalOpen === "add" ? "Add Skill" : "Edit Skill"}
                  </h4>
                </div>

                <div className="d-flex justify-content-end">
                  <i
                    className="fa-solid fa-xmark cursor text-muted"
                    onClick={closeModal}
                  ></i>
                </div>
              </div>
            </CardHeader>

            <Form onSubmit={(e) => e.preventDefault()}>
              <CardBody>
                <Row>
                  <Col md="6" className="mb-3">
                    <FormGroup>
                      <Label>
                        Skill Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        name="name"
                        type="text"
                        placeholder="Enter skill name"
                        value={formData.name.value}
                        onChange={(e) =>
                          handleInputChange(e.target.name, e.target.value)
                        }
                        onBlur={(e) =>
                          handleOnBlur(e.target.name, e.target.value)
                        }
                        className={formData.name.error ? "is-invalid" : ""}
                        minLength={3}
                        maxLength={150}
                      />
                      {formData.name.error && (
                        <small className="invalid-feedback">
                          {formData.name.error}
                        </small>
                      )}
                    </FormGroup>
                  </Col>

                  <Col md="6" className="mb-3">
                    <FormGroup>
                      <Label>
                        Status <span className="text-danger">*</span>
                      </Label>
                      <div className="radio-rapper mt-2">
                        <FormGroup check inline>
                          <Label check>
                            <Input
                              type="radio"
                              name="status"
                              value="ACTIVE"
                              className="cursor"
                              checked={formData.status.value === "ACTIVE"}
                              onChange={(e) =>
                                handleInputChange("status", e.target.value)
                              }
                            />{" "}
                            Active
                          </Label>
                        </FormGroup>
                        <FormGroup check inline>
                          <Label check>
                            <Input
                              type="radio"
                              name="status"
                              value="INACTIVE"
                              className="cursor"
                              checked={formData.status.value === "INACTIVE"}
                              onChange={(e) =>
                                handleInputChange("status", e.target.value)
                              }
                            />{" "}
                            Inactive
                          </Label>
                        </FormGroup>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>

              <CardFooter className="text-right">
                <Button
                  color="secondary"
                  onClick={closeModal}
                  className="save-btn-sm"
                >
                  Cancel
                </Button>
                <Button
                  color="deep-blue"
                  onClick={handleSubmit}
                  disabled={loaderOnClick}
                  className="save-btn-sm"
                >
                  {loaderOnClick ? (
                    <>
                      <i className="fa fa-spinner fa-spin fa-lg text-white" />
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </CardFooter>
            </Form>
          </Card>
        </RightPanelCard>
      </Container>
    </>
  );
}

export default Skills;
